import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, ChevronLeft } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = 'https://baustellenmgmt.mas123.de/api';

function formatDate(date) {
  // Verhindere Timezone-Probleme durch direkte Formatierung
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getCurrentWeek() {
  const today = new Date();
  const monday = new Date(today);
  monday.setDate(today.getDate() - today.getDay() + 1);
  return formatDate(monday);
}

function getWeekdayLabel(date, weeksToShow) {
  if (weeksToShow > 3) {
    const shortName = date.toLocaleDateString('de-DE', { weekday: 'short' }).replace('.', '');
    return shortName.slice(0, 2);
  }
  return date.toLocaleDateString('de-DE', { weekday: 'long' });
}

function getWeekDates(startDate, weeksToShow) {
  const dates = [];
  const start = new Date(startDate);
  const daysToShow = Math.max(1, weeksToShow) * 7;
  for (let i = 0; i < daysToShow; i++) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    dates.push({
      date: formatDate(date),
      dayName: getWeekdayLabel(date, weeksToShow),
      dayNumber: date.getDate(),
      month: date.toLocaleDateString('de-DE', { month: 'short' }),
      isToday: formatDate(date) === formatDate(new Date())
    });
  }
  return dates;
}

function getWeekNumber(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

function compareByLabelDe(a, b) {
  return String(a || '').localeCompare(String(b || ''), 'de', { sensitivity: 'base' });
}

function KalenderView({ token }) {
  const [selectedWeek, setSelectedWeek] = useState(getCurrentWeek());
  const [weeksToShow, setWeeksToShow] = useState(() => {
    const stored = localStorage.getItem('calendarDefaultWeeks');
    const parsed = stored ? parseInt(stored, 10) : 2;
    return Number.isNaN(parsed) ? 2 : Math.max(1, parsed);
  });
  const [showWeekends, setShowWeekends] = useState(false);
  const [showEmployees, setShowEmployees] = useState(() => {
    const stored = localStorage.getItem('calendarShowEmployees');
    return stored ? stored === 'true' : true;
  });
  const [showSubunternehmer, setShowSubunternehmer] = useState(() => {
    const stored = localStorage.getItem('calendarShowSubunternehmer');
    return stored ? stored === 'true' : true;
  });
  const [mitarbeiter, setMitarbeiter] = useState([]);
  const [subunternehmer, setSubunternehmer] = useState([]);
  const [baustellen, setBaustellen] = useState([]);
  const [einsaetze, setEinsaetze] = useState([]);
  const [actionMode, setActionMode] = useState(null);
  const [selectedBaustelleId, setSelectedBaustelleId] = useState('');
  const [taetigkeit, setTaetigkeit] = useState('');
  const [copySource, setCopySource] = useState(null);
  const [moveSource, setMoveSource] = useState(null);
  const [events, setEvents] = useState([]);
  const [calendars, setCalendars] = useState([]);

  const loadCalendars = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/kalender/external-calendars/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCalendars(response.data.results || response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Kalender:', error);
    }
  }, [token]);

  const loadEvents = useCallback(async () => {
    try {
      const weekDates = getWeekDates(selectedWeek, weeksToShow);
      const startDate = weekDates[0].date;
      const endDate = weekDates[weekDates.length - 1].date;

      // Lade alle Events im Datumsbereich
      const response = await axios.get(`${API_BASE_URL}/kalender/calendar-events/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Filtere Events für den aktuellen Zeitraum
      const allEvents = response.data.results || response.data;
      const weekEvents = allEvents.filter(event => {
        return event.start_date <= endDate && event.end_date >= startDate;
      });

      setEvents(weekEvents);
    } catch (error) {
      console.error('Fehler beim Laden der Events:', error);
    }
  }, [selectedWeek, weeksToShow, token]);

  const loadMitarbeiter = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/core/users/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setMitarbeiter(response.data.results || response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Mitarbeiter:', error);
    }
  }, [token]);

  const loadSubunternehmer = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/core/subunternehmer/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSubunternehmer(response.data.results || response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Subunternehmer:', error);
    }
  }, [token]);

  const loadBaustellen = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/baustellen/baustellen/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setBaustellen(response.data.results || response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Baustellen:', error);
    }
  }, [token]);

  const loadEinsaetze = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/kalender/einsatzplanung/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setEinsaetze(response.data.results || response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Einsaetze:', error);
    }
  }, [token]);

  useEffect(() => {
    loadCalendars();
    loadEvents();
    loadMitarbeiter();
    loadSubunternehmer();
    loadBaustellen();
    loadEinsaetze();
  }, [selectedWeek, loadCalendars, loadEvents, loadMitarbeiter, loadSubunternehmer, loadBaustellen, loadEinsaetze]);

  function changeWeek(direction) {
    const current = new Date(selectedWeek);
    current.setDate(current.getDate() + (direction * 7 * weeksToShow));
    setSelectedWeek(formatDate(current));
  }

  function goToCurrentWeek() {
    setSelectedWeek(getCurrentWeek());
  }

  function getEventsForDate(date) {
    return events.filter(event => {
      // Wenn "Nur Startdatum verwenden" aktiv ist, nur am Startdatum anzeigen
      if (event.calendar_use_start_date_only) {
        return event.start_date === date;
      }
      // Sonst über den gesamten Zeitraum anzeigen
      return event.start_date <= date && event.end_date >= date;
    });
  }

  function getCalendarColor(calendarId) {
    const calendar = calendars.find(c => c.id === calendarId);
    return calendar?.color || '#3b82f6';
  }

  function getCalendarFontSize(calendarId) {
    const calendar = calendars.find(c => c.id === calendarId);
    const size = calendar?.event_font_size;
    return Number.isFinite(size) ? size : 6;
  }

function getCalendarName(calendarId) {
  const calendar = calendars.find(c => c.id === calendarId);
  return calendar?.name || 'Kalender';
}

  function getContrastTextColor(hex) {
    if (!hex) return '#111827';
    const safe = hex.startsWith('#') ? hex.slice(1) : hex;
    if (safe.length !== 6) return '#111827';
    const num = parseInt(safe, 16);
    if (Number.isNaN(num)) return '#111827';
    const r = num >> 16;
    const g = (num >> 8) & 0xff;
    const b = num & 0xff;
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b);
    return luminance > 160 ? '#111827' : '#ffffff';
  }

  function getPersonLabel(person) {
    if (person.type === 'subunternehmer' || person.firma) {
      return person.firma || `Subunternehmer ${person.id}`;
    }
    const fullName = `${person.first_name || ''} ${person.last_name || ''}`.trim();
    return fullName || person.username || person.email || `Mitarbeiter ${person.id}`;
  }

  function buildPersonPayload(person) {
    return person.type === 'subunternehmer'
      ? { mitarbeiter: null, subunternehmer: person.id }
      : { mitarbeiter: person.id, subunternehmer: null };
  }

  async function createEinsatzForDay(person, dateStr) {
    if (!actionMode) return;
    if (actionMode === 'baustelle' && !selectedBaustelleId) {
      alert('Bitte zuerst eine Baustelle waehlen.');
      return;
    }

    const payload = {
      ...buildPersonPayload(person),
      start_datum: dateStr,
      ende_datum: dateStr,
      ganztags: true,
      einsatz_typ: actionMode === 'urlaub' ? 'URLAUB' : actionMode === 'krank' ? 'KRANK' : 'ARBEIT',
      baustelle: actionMode === 'baustelle' ? selectedBaustelleId : null,
      beschreibung: actionMode === 'baustelle' ? taetigkeit : ''
    };

    try {
      await axios.post(`${API_BASE_URL}/kalender/einsatzplanung/`, payload, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadEinsaetze();
    } catch (error) {
      console.error('Fehler beim Eintragen:', error);
      alert('Eintrag konnte nicht erstellt werden.');
    }
  }

  async function deleteEinsatz(einsatz) {
    try {
      await axios.delete(`${API_BASE_URL}/kalender/einsatzplanung/${einsatz.id}/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadEinsaetze();
    } catch (error) {
      console.error('Fehler beim Loeschen:', error);
      alert('Eintrag konnte nicht geloescht werden.');
    }
  }

  async function moveEinsatzToDay(einsatz, person, dateStr) {
    try {
      await axios.patch(`${API_BASE_URL}/kalender/einsatzplanung/${einsatz.id}/`, {
        ...buildPersonPayload(person),
        start_datum: dateStr,
        ende_datum: dateStr
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadEinsaetze();
    } catch (error) {
      console.error('Fehler beim Verschieben:', error);
      alert('Eintrag konnte nicht verschoben werden.');
    }
  }

  async function copyEinsatzToDay(einsatz, person, dateStr) {
    const payload = {
      ...buildPersonPayload(person),
      start_datum: dateStr,
      ende_datum: dateStr,
      ganztags: einsatz.ganztags,
      einsatz_typ: einsatz.einsatz_typ,
      baustelle: einsatz.baustelle || null,
      beschreibung: einsatz.beschreibung || ''
    };

    try {
      await axios.post(`${API_BASE_URL}/kalender/einsatzplanung/`, payload, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadEinsaetze();
    } catch (error) {
      console.error('Fehler beim Kopieren:', error);
      alert('Eintrag konnte nicht kopiert werden.');
    }
  }

  function handleEntryClick(einsatz, event) {
    event.stopPropagation();
    if (actionMode === 'delete') {
      if (!window.confirm('Diesen Eintrag loeschen?')) return;
      deleteEinsatz(einsatz);
      return;
    }
    if (actionMode === 'copy') {
      setCopySource(einsatz);
      return;
    }
    if (actionMode === 'move') {
      setMoveSource(einsatz);
    }
  }

  function handleCellClick(person, dateStr) {
    if (!actionMode) return;
    if (actionMode === 'copy') {
      if (!copySource) return;
      copyEinsatzToDay(copySource, person, dateStr);
      return;
    }
    if (actionMode === 'move') {
      if (!moveSource) return;
      moveEinsatzToDay(moveSource, person, dateStr);
      setMoveSource(null);
      return;
    }
    createEinsatzForDay(person, dateStr);
  }

  function getEinsatzForDay(person, dateStr) {
    return einsaetze.filter(e => {
      const matchesPerson = person.type === 'subunternehmer'
        ? e.subunternehmer === person.id
        : e.mitarbeiter === person.id;
      return matchesPerson && e.start_datum <= dateStr && e.ende_datum >= dateStr;
    });
  }

  function getEinsatzLabel(einsatz) {
    if (einsatz.einsatz_typ === 'URLAUB') return 'Urlaub';
    if (einsatz.einsatz_typ === 'KRANK') return 'Krankheit';
    if (einsatz.baustelle) {
      const baustelle = baustellen.find(b => b.id === einsatz.baustelle);
      const name = baustelle ? baustelle.name : 'Baustelle';
      const taetigkeitText = einsatz.beschreibung ? ` - ${einsatz.beschreibung}` : '';
      return `${name}${taetigkeitText}`;
    }
    return 'Arbeit';
  }

  function getCellContent(einsaetzeForDay) {
    if (!einsaetzeForDay || einsaetzeForDay.length === 0) {
      return { label: '', color: '#f9fafb', border: '#e5e7eb' };
    }
    const labels = einsaetzeForDay.map(getEinsatzLabel).join(' / ');
    const first = einsaetzeForDay[0];
    const baustelle = baustellen.find(b => b.id === first.baustelle);
    const urlaubColor = localStorage.getItem('urlaubColor') || '#e0f2fe';
    const krankColor = localStorage.getItem('krankColor') || '#fee2e2';
    const bgColor = first.einsatz_typ === 'URLAUB'
      ? urlaubColor
      : first.einsatz_typ === 'KRANK'
        ? krankColor
        : (baustelle?.farbe || '#e0f2fe');
    const borderColor = baustelle?.farbe ? '#0f172a' : '#bae6fd';
    return { label: labels, color: bgColor, border: borderColor };
  }

  const weekDates = getWeekDates(selectedWeek, weeksToShow);
  const visibleWeekDates = weekDates.filter(day => {
    if (showWeekends) return true;
    const dayOfWeek = new Date(day.date).getDay();
    return dayOfWeek !== 0 && dayOfWeek !== 6;
  });
  const extraWeeks = Math.max(0, weeksToShow - 4);
  const sizeAdjust = extraWeeks <= 2
    ? extraWeeks * 0.125
    : (2 * 0.125) + ((extraWeeks - 2) * 0.25);
  const weekDayFontSize = `${Math.max(0.55, 0.875 - sizeAdjust)}rem`;
  const dayNumberFontSize = `${Math.max(0.9, 1.5 - sizeAdjust)}rem`;
  const selectedDateObj = new Date(selectedWeek);
  const aktuelleKW = getWeekNumber(selectedDateObj);
  const endDateObj = new Date(weekDates[weekDates.length - 1].date);
  const endKW = getWeekNumber(endDateObj);
  const aktuellesJahr = selectedDateObj.getFullYear();
  const totalColumns = visibleWeekDates.length + ((showEmployees || showSubunternehmer) ? 1 : 0);

  const monthSegments = visibleWeekDates.reduce((segments, day) => {
    const dateObj = new Date(day.date);
    const label = dateObj.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
    if (segments.length === 0 || segments[segments.length - 1].label !== label) {
      segments.push({ label, span: 1 });
    } else {
      segments[segments.length - 1].span += 1;
    }
    return segments;
  }, []);

  const weekSegments = [];
  visibleWeekDates.forEach(day => {
    const weekNumber = getWeekNumber(new Date(day.date));
    if (!weekSegments.length || weekSegments[weekSegments.length - 1].weekNumber !== weekNumber) {
      weekSegments.push({ weekNumber, label: `KW ${weekNumber}`, span: 1 });
    } else {
      weekSegments[weekSegments.length - 1].span += 1;
    }
  });

  const mitarbeiterPersonen = mitarbeiter.map(m => ({ ...m, type: 'mitarbeiter', label: getPersonLabel(m) }));
  const subunternehmerPersonen = subunternehmer.map(s => ({ ...s, type: 'subunternehmer', label: getPersonLabel(s) }));
  mitarbeiterPersonen.sort((a, b) => compareByLabelDe(a.label, b.label));
  subunternehmerPersonen.sort((a, b) => compareByLabelDe(a.label, b.label));
  const sortedBaustellen = [...baustellen].sort((a, b) => (
    compareByLabelDe(a.kunde_name || a.name, b.kunde_name || b.name) || compareByLabelDe(a.nummer, b.nummer)
  ));

  return (
    <div>
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Calendar size={28} />
            Kalender
          </h2>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              onClick={() => {
                setActionMode('baustelle');
                setCopySource(null);
                setMoveSource(null);
              }}
              className={`btn ${actionMode === 'baustelle' ? 'btn-primary' : ''}`}
            >
              Baustelle zuordnen
            </button>
            <button
              onClick={() => {
                setActionMode('urlaub');
                setCopySource(null);
                setMoveSource(null);
              }}
              className={`btn ${actionMode === 'urlaub' ? 'btn-primary' : ''}`}
            >
              Urlaub eintragen
            </button>
            <button
              onClick={() => {
                setActionMode('krank');
                setCopySource(null);
                setMoveSource(null);
              }}
              className={`btn ${actionMode === 'krank' ? 'btn-primary' : ''}`}
            >
              Krankheit eintragen
            </button>
            <button
              onClick={() => {
                setActionMode('delete');
                setCopySource(null);
                setMoveSource(null);
              }}
              className={`btn ${actionMode === 'delete' ? 'btn-primary' : ''}`}
            >
              Löschen
            </button>
            <button
              onClick={() => {
                setActionMode('copy');
                setCopySource(null);
                setMoveSource(null);
              }}
              className={`btn ${actionMode === 'copy' ? 'btn-primary' : ''}`}
            >
              Eintrag kopieren
            </button>
            <button
              onClick={() => {
                setActionMode('move');
                setCopySource(null);
                setMoveSource(null);
              }}
              className={`btn ${actionMode === 'move' ? 'btn-primary' : ''}`}
            >
              Verschieben
            </button>
            <button
              onClick={() => {
                setActionMode(null);
                setSelectedBaustelleId('');
                setTaetigkeit('');
                setCopySource(null);
                setMoveSource(null);
              }}
              className="btn"
            >
              Eintragen stoppen
            </button>
          </div>
        </div>

        {actionMode === 'baustelle' && (
          <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              className="input"
              value={selectedBaustelleId}
              onChange={(e) => setSelectedBaustelleId(e.target.value)}
              style={{ maxWidth: '320px' }}
            >
              <option value="">Baustelle wählen...</option>
              {sortedBaustellen.map(b => (
                <option
                  key={b.id}
                  value={b.id}
                  style={{ backgroundColor: b.farbe || '#3B82F6', color: '#111827' }}
                >
                  {b.name}
                </option>
              ))}
            </select>
            <input
              type="text"
              className="input"
              placeholder="Tätigkeit"
              value={taetigkeit}
              onChange={(e) => setTaetigkeit(e.target.value)}
              style={{ maxWidth: '280px' }}
            />
          </div>
        )}

        {/* Kalender-Legende entfernt */}

        {/* Wochennavigation */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <button onClick={() => changeWeek(-1)} className="btn" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <ChevronLeft size={18} />
            Vorherige
          </button>

          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', fontSize: '1.125rem' }}>
              KW {aktuelleKW}{weeksToShow > 1 ? ` - ${endKW}` : ''} / {aktuellesJahr}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              {weekDates[0].dayNumber}. {weekDates[0].month} - {weekDates[weekDates.length - 1].dayNumber}. {weekDates[weekDates.length - 1].month}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => setWeeksToShow(prev => Math.max(1, prev - 1))}
              className="btn"
            >
              Weniger Wochen anzeigen
            </button>
            <button
              onClick={() => {
                const stored = localStorage.getItem('calendarDefaultWeeks');
                const parsed = stored ? parseInt(stored, 10) : 2;
                setWeeksToShow(Number.isNaN(parsed) ? 2 : Math.max(1, parsed));
              }}
              className="btn"
            >
              Normale Anzeige
            </button>
            <button
              onClick={() => setWeeksToShow(prev => prev + 1)}
              className="btn"
            >
              Mehr Wochen anzeigen
            </button>
            <button onClick={goToCurrentWeek} className="btn">
              Heute
            </button>
            <button onClick={() => changeWeek(1)} className="btn" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              Nächste
              <ChevronLeft size={18} style={{ transform: 'rotate(180deg)' }} />
            </button>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '0.5rem', fontSize: '0.875rem' }}>
              <input
                type="checkbox"
                checked={showWeekends}
                onChange={(e) => setShowWeekends(e.target.checked)}
                style={{ width: '1.1rem', height: '1.1rem', cursor: 'pointer' }}
              />
              Samstage/Sonntage anzeigen
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '0.5rem', fontSize: '0.875rem' }}>
              <input
                type="checkbox"
                checked={showEmployees}
                onChange={(e) => setShowEmployees(e.target.checked)}
                style={{ width: '1.1rem', height: '1.1rem', cursor: 'pointer' }}
              />
              Mitarbeiter anzeigen
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '0.5rem', fontSize: '0.875rem' }}>
              <input
                type="checkbox"
                checked={showSubunternehmer}
                onChange={(e) => setShowSubunternehmer(e.target.checked)}
                style={{ width: '1.1rem', height: '1.1rem', cursor: 'pointer' }}
              />
              Subunternehmer anzeigen
            </label>
          </div>
        </div>

        {/* Wochenübersicht */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${totalColumns}, 1fr)`,
          gap: '0.75rem'
        }}>
          {(showEmployees || showSubunternehmer) && <div />}
          {monthSegments.map((segment, index) => (
            <div
              key={`month-${index}`}
              style={{
                gridColumn: `span ${segment.span}`,
                background: '#eef2ff',
                border: '1px solid #e5e7eb',
                borderRadius: '0.5rem',
                padding: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                textAlign: 'center',
                color: '#1f2937'
              }}
            >
              {segment.label}
            </div>
          ))}

          {(showEmployees || showSubunternehmer) && <div />}
          {weekSegments.map((segment, index) => (
            <div
              key={`week-${index}`}
              style={{
                gridColumn: `span ${segment.span}`,
                background: '#f3f4f6',
                border: '1px solid #e5e7eb',
                borderRadius: '0.5rem',
                padding: '0.4rem',
                fontSize: '0.8rem',
                fontWeight: '600',
                textAlign: 'center',
                color: '#374151'
              }}
            >
              {segment.label}
            </div>
          ))}

          {(showEmployees || showSubunternehmer) && <div />}
          {visibleWeekDates.map(day => {
            const dayEvents = getEventsForDate(day.date);
            const dayOfWeek = new Date(day.date).getDay();
            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
            return (
              <div
                key={day.date}
                style={{
                  background: day.isToday ? '#eff6ff' : (isWeekend ? '#e5e7eb' : '#f9fafb'),
                  border: day.isToday ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  padding: '0.5rem',
                  minHeight: '75px'
                }}
              >
                <div style={{
                  fontWeight: '600',
                  fontSize: weekDayFontSize,
                  color: day.isToday ? '#1e40af' : '#374151',
                  marginBottom: '0.25rem'
                }}>
                  {day.dayName}
                </div>
                <div style={{
                  fontSize: dayNumberFontSize,
                  fontWeight: 'bold',
                  color: day.isToday ? '#1e40af' : '#111827',
                  marginBottom: '0.5rem'
                }}>
                  {day.dayNumber}
                </div>

                {!(showEmployees || showSubunternehmer) && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    {dayEvents.map(event => (
                      <div
                        key={event.id}
                        style={{
                          background: getCalendarColor(event.calendar),
                          color: getContrastTextColor(getCalendarColor(event.calendar)),
                          padding: '0.25rem 0.5rem',
                          borderRadius: '0.25rem',
                          fontSize: `${getCalendarFontSize(event.calendar)}px`,
                          fontWeight: '500',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={`${event.title} (${getCalendarName(event.calendar)})`}
                      >
                        {event.title}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {(showEmployees || showSubunternehmer) && (
          <div style={{
            marginTop: '0.75rem',
            maxHeight: '60vh',
            overflowY: 'auto'
          }}>
            {showEmployees && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: `200px repeat(${visibleWeekDates.length}, 1fr)`,
                gap: '0.5rem',
                alignItems: 'stretch'
              }}>
                {mitarbeiterPersonen.map((person) => (
                  <React.Fragment key={`${person.type}-${person.id}`}>
                    <div style={{
                      background: '#f3f4f6',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0.5rem',
                      padding: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#374151',
                      alignSelf: 'stretch'
                    }}>
                      <div>{person.label}</div>
                    </div>
                    {actionMode ? (
                      visibleWeekDates.map(day => (
                        <div
                          key={`${person.type}-${person.id}-${day.date}`}
                          style={{
                            background: '#f9fafb',
                            border: '1px solid #e5e7eb',
                            borderRadius: '0.5rem',
                            minHeight: '26px',
                            cursor: 'pointer',
                            padding: '0.25rem'
                          }}
                          onClick={() => handleCellClick(person, day.date)}
                        >
                          {getEinsatzForDay(person, day.date).map((einsatz) => {
                            const baustelle = baustellen.find(b => b.id === einsatz.baustelle);
                            const urlaubColor = localStorage.getItem('urlaubColor') || '#e0f2fe';
                            const krankColor = localStorage.getItem('krankColor') || '#fee2e2';
                            const bgColor = einsatz.einsatz_typ === 'URLAUB'
                              ? urlaubColor
                              : einsatz.einsatz_typ === 'KRANK'
                                ? krankColor
                                : (baustelle?.farbe || '#e0f2fe');
                            const borderColor = baustelle?.farbe ? '#0f172a' : '#bae6fd';
                            const textColor = getContrastTextColor(bgColor);
                            return (
                              <div
                                key={einsatz.id}
                                style={{
                                  background: bgColor,
                                  border: `1px solid ${borderColor}`,
                                  borderRadius: '0.25rem',
                                  padding: '0.2rem 0.3rem',
                                  fontSize: '0.7rem',
                                  marginBottom: '0.2rem',
                                  color: textColor
                                }}
                                title={getEinsatzLabel(einsatz)}
                                onClick={(event) => handleEntryClick(einsatz, event)}
                              >
                                {getEinsatzLabel(einsatz)}
                              </div>
                            );
                          })}
                        </div>
                      ))
                    ) : (
                      (() => {
                        const segments = [];
                        let current = null;
                        visibleWeekDates.forEach((day) => {
                          const dayEntries = getEinsatzForDay(person, day.date);
                          const content = getCellContent(dayEntries);
                          if (!current || current.label !== content.label) {
                            if (current) segments.push(current);
                            current = { ...content, span: 1 };
                          } else {
                            current.span += 1;
                          }
                        });
                        if (current) segments.push(current);

                        return segments.map((segment, index) => (
                          <div
                            key={`${person.type}-${person.id}-seg-${index}`}
                            style={{
                              gridColumn: `span ${segment.span}`,
                              background: segment.color,
                              border: `1px solid ${segment.border}`,
                              borderRadius: '0.5rem',
                              minHeight: '26px',
                              padding: '0.25rem',
                              fontSize: '0.7rem',
                              color: getContrastTextColor(segment.color)
                            }}
                          >
                            {segment.label}
                          </div>
                        ));
                      })()
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
            {showEmployees && showSubunternehmer && (
              <div style={{ height: '0.75rem' }} />
            )}
            {showSubunternehmer && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: `200px repeat(${visibleWeekDates.length}, 1fr)`,
                gap: '0.5rem',
                alignItems: 'stretch'
              }}>
                {subunternehmerPersonen.map((person) => (
                  <React.Fragment key={`${person.type}-${person.id}`}>
                    <div style={{
                      background: '#f3f4f6',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0.5rem',
                      padding: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#374151',
                      alignSelf: 'stretch'
                    }}>
                      <div>{person.label}</div>
                    </div>
                    {actionMode ? (
                      visibleWeekDates.map(day => (
                        <div
                          key={`${person.type}-${person.id}-${day.date}`}
                          style={{
                            background: '#f9fafb',
                            border: '1px solid #e5e7eb',
                            borderRadius: '0.5rem',
                            minHeight: '26px',
                            cursor: 'pointer',
                            padding: '0.25rem'
                          }}
                          onClick={() => handleCellClick(person, day.date)}
                        >
                          {getEinsatzForDay(person, day.date).map((einsatz) => {
                            const baustelle = baustellen.find(b => b.id === einsatz.baustelle);
                            const urlaubColor = localStorage.getItem('urlaubColor') || '#e0f2fe';
                            const krankColor = localStorage.getItem('krankColor') || '#fee2e2';
                            const bgColor = einsatz.einsatz_typ === 'URLAUB'
                              ? urlaubColor
                              : einsatz.einsatz_typ === 'KRANK'
                                ? krankColor
                                : (baustelle?.farbe || '#e0f2fe');
                            const borderColor = baustelle?.farbe ? '#0f172a' : '#bae6fd';
                            const textColor = getContrastTextColor(bgColor);
                            return (
                              <div
                                key={einsatz.id}
                                style={{
                                  background: bgColor,
                                  border: `1px solid ${borderColor}`,
                                  borderRadius: '0.25rem',
                                  padding: '0.2rem 0.3rem',
                                  fontSize: '0.7rem',
                                  marginBottom: '0.2rem',
                                  color: textColor
                                }}
                                title={getEinsatzLabel(einsatz)}
                                onClick={(event) => handleEntryClick(einsatz, event)}
                              >
                                {getEinsatzLabel(einsatz)}
                              </div>
                            );
                          })}
                        </div>
                      ))
                    ) : (
                      (() => {
                        const segments = [];
                        let current = null;
                        visibleWeekDates.forEach((day) => {
                          const dayEntries = getEinsatzForDay(person, day.date);
                          const content = getCellContent(dayEntries);
                          if (!current || current.label !== content.label) {
                            if (current) segments.push(current);
                            current = { ...content, span: 1 };
                          } else {
                            current.span += 1;
                          }
                        });
                        if (current) segments.push(current);

                        return segments.map((segment, index) => (
                          <div
                            key={`${person.type}-${person.id}-seg-${index}`}
                            style={{
                              gridColumn: `span ${segment.span}`,
                              background: segment.color,
                              border: `1px solid ${segment.border}`,
                              borderRadius: '0.5rem',
                              minHeight: '26px',
                              padding: '0.25rem',
                              fontSize: '0.7rem',
                              color: getContrastTextColor(segment.color)
                            }}
                          >
                            {segment.label}
                          </div>
                        ));
                      })()
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Event-Liste entfernt */}
    </div>
  );
}

export default KalenderView;
